import { useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, FileVideo, ImageOff } from "lucide-react";

const ACCEPT = {
  image: "image/*",
  video: "video/mp4,video/webm,video/quicktime",
  any: "image/*,video/mp4,video/webm,video/quicktime",
};

export const ProofField = ({ media = "image", value, kind, onChange, testid = "proof-field" }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isVideo = (file.type || "").startsWith("video/");
    const cap = isVideo ? 64 : 8;
    if (file.size > cap * 1024 * 1024) {
      toast.error(`That file is ${(file.size / 1048576).toFixed(1)}MB — keep it under ${cap}MB`);
      return;
    }
    const body = new FormData();
    body.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/uploads/proof", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url, data.content_type);
      toast.success("Proof uploaded");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const isVideo = (kind || "").startsWith("video/") || /\.(mp4|webm|mov)$/i.test(value || "");

  return (
    <div data-testid={testid}>
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT[media] || ACCEPT.any}
        className="hidden"
        onChange={pick}
        data-testid={`${testid}-input`}
      />
      <Button
        type="button"
        variant="outline"
        className="w-full rounded-none"
        disabled={busy}
        onClick={() => fileRef.current?.click()}
        data-testid={`${testid}-upload-btn`}
      >
        <Upload className="mr-2 h-4 w-4" />
        {busy ? "Uploading…" : media === "video" ? "Upload video clip" : "Upload screenshot or clip"}
      </Button>

      <div
        className="mt-3 flex h-44 w-full items-center justify-center overflow-hidden border border-border bg-secondary/40"
        data-testid={`${testid}-preview`}
      >
        {!value ? (
          <div className="flex flex-col items-center gap-2 text-xs text-muted-foreground">
            <ImageOff className="h-5 w-5" />
            {media === "video" ? "MP4, WebM or MOV up to 64MB" : "JPG, PNG or a clip up to 64MB"}
          </div>
        ) : isVideo ? (
          <video src={value} controls className="h-full w-full object-contain" data-testid={`${testid}-video`}>
            <track kind="captions" />
          </video>
        ) : (
          <img src={value} alt="proof" className="h-full w-full object-contain" data-testid={`${testid}-img`} />
        )}
      </div>
      {value && (
        <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <FileVideo className="h-3.5 w-3.5" /> Attached — staff will review it.
        </p>
      )}
    </div>
  );
};
